import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Loader2, Megaphone } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

export default function Contact() {
  useSeo({ title: "Contact & Advertise", description: "Get in touch with the Mandalay+ team for support, partnerships and advertising." });
  const [form, setForm] = useState({ name: "", email: "", subject: "Advertising", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/support", form);
      setSent(true);
      toast.success("Message sent to the Mandalay+ team");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally { setLoading(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = "w-full bg-ink border border-white/10 rounded-md px-4 py-3 outline-none focus:ring-1 focus:ring-brand";

  return (
    <div className="bg-ink min-h-screen pt-24 px-4 sm:px-12 pb-20">
      <div className="max-w-2xl mx-auto animate-fade-up">
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-2 flex items-center gap-3"><Megaphone className="w-9 h-9 text-gold" /> Contact & Advertise</h1>
        <p className="text-white/60 mb-2">For support, content partnerships or advertising on Mandalay+, send us a message.</p>
        <p className="text-white/50 text-sm mb-8 flex items-center gap-2"><Mail className="w-4 h-4 text-gold" /> admin@mandalaymedia.com</p>

        {sent ? (
          <div data-testid="contact-success" className="bg-surface border border-white/10 rounded-xl p-8 text-center">
            <p className="text-gold font-display text-xl mb-2">Thank you!</p>
            <p className="text-white/70">Your message has reached our team. We'll get back to you at {form.email}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 bg-surface border border-white/10 rounded-xl p-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={inp} />
              <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className={inp} />
            </div>
            <select data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className={inp}>
              <option>Advertising</option>
              <option>Content Partnership</option>
              <option>Support</option>
              <option>General</option>
            </select>
            <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Your message..." className={inp} />
            <button data-testid="contact-submit" disabled={loading} className="bg-brand hover:bg-brand/90 transition-colors rounded-md font-semibold px-6 py-3 flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Message
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
